import React, { useCallback, useEffect, useMemo, type ReactElement } from 'react';
import { Flex } from '@atlaskit/primitives/compiled';
import { ExpandableMenuItemContent, ExpandableMenuItemTrigger } from '@atlaskit/navigation-system';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { SECTION_ITEM_PLANS } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_PLANS } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { SkeletonNavMenuItems } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import { getTestId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useSectionItemAnalytics } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { useExpandableEntryPointTrigger } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/controllers/use-expandable-entry-point-trigger/index.tsx';
import {
	PACKAGE_NAME,
	TEAM_NAME,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-plans/src/ui/constants.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { PaidFeatureAwarenessRenderer } from '@atlassian/jira-paid-feature-awareness-renderer/src/ui/PaidFeatureAwarenessRendererComponent.tsx';
import { PaidFeatureAwarenessPremiumLozenge } from '@atlassian/jira-paid-feature-awareness-renderer/src/ui/lozenges/index.tsx';
import { useMemoWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/hooks/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import { Nav4PlansFooter } from '../common/footer/Nav4PlansFooter.tsx';
import { asyncPlansSectionPlanListViewQuery } from './async-plans-list/entrypoint.tsx';

type Props = Nav4DragAndDropMenuItemProps & {
	actionsOnHover: ReactElement;
	isExpanded: boolean;
};

export const Nav4PlansDefault: React.FC<Props> = componentWithFG(
	'navx-global-side-nav',
	Nav4PlansDefaultGSN,
	Nav4PlansDefaultNav4,
);

function Nav4PlansDefaultGSN(props: Props) {
	const {
		actionsOnHover,
		draggableRef,
		dropIndicator,
		hasDragIndicator,
		isDragging,
		isExpanded,
		onExpandFlyoutToggle,
		visualContentRef,
	} = props;
	const cloudId = useCloudId();

	let accountId: string | undefined;
	let activationId: string | undefined;
	if (expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false)) {
		const {
			data: {
				user: { accountId: accountIdFromUser },
			},
			// eslint-disable-next-line react-hooks/rules-of-hooks
		} = useCurrentUser();

		// eslint-disable-next-line react-hooks/rules-of-hooks
		activationId = useActivationId();
		accountId = accountIdFromUser || undefined;
	}
	const entryPointParams = useMemo(
		() => ({ cloudId, accountId, activationId }),
		[cloudId, accountId, activationId],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncPlansSectionPlanListViewQuery,
		entryPointParams,
	);
	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.PLANS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();
	const menuId = MENU_ID_PLANS;

	// remove the entire useEffect when cleaning up nav4-show-plans-when-hidden-by-default
	// we should be using the route based entry point approach instead
	// https://hello.atlassian.net/wiki/spaces/UAF/pages/2754170095/Tutorial+EntryPoints+in+Routing
	// If starting life expanded, make sure to load.
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (isExpanded) {
			entryPointActions.load();
		}
	}, [isExpanded, entryPointActions]);

	const handleSystemToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
			}
		},
		[entryPointActions],
	);

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.PLANS,
					sectionItem: SECTION_ITEM_PLANS,
				});
			}
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[entryPointActions, onExpandFlyoutToggle, sendSectionItemAnalyticsEvent],
	);

	const premiumLozenge = useMemoWithCondition(
		() => <PaidFeatureAwarenessPremiumLozenge />,
		[],
		() => fg('jfp-magma-4016-fix-side-nav-ssr-hydration'),
	);

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle}
			onSystemToggle={handleSystemToggle}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				aria-label={formattedMessage}
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				interactionName="nav4-sidebar-plans-default"
				isDragging={isDragging}
				ref={draggableRef ? mergeRefs(triggerRef, draggableRef) : triggerRef}
				testId={getTestId(menuId)}
			>
				<Flex gap="space.100" alignItems="center">
					{formattedMessage}
					<PaidFeatureAwarenessRenderer component={premiumLozenge} placement="navigation.plans" />
				</Flex>
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<JiraEntryPointContainer
					// Data stream, will subscribe to load updates
					entryPointReferenceSubject={entryPointReferenceSubject}
					// Required props for error JSErrorBoundary.
					id="nav4-plans-default"
					packageName={PACKAGE_NAME}
					// Optional props for error JSErrorBoundary.
					errorFallback="flag"
					teamName={TEAM_NAME}
					// Fallback for Suspense
					fallback={<SkeletonNavMenuItems />}
					// Props for the root component
					runtimeProps={entryPointParams}
				/>
				<Nav4PlansFooter />
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

function Nav4PlansDefaultNav4({ actionsOnHover, isExpanded }: Props) {
	const cloudId = useCloudId();

	let accountId: string | undefined;
	let activationId: string | undefined;
	if (expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false)) {
		const {
			data: {
				user: { accountId: accountIdFromUser },
			},
			// eslint-disable-next-line react-hooks/rules-of-hooks
		} = useCurrentUser();

		// eslint-disable-next-line react-hooks/rules-of-hooks
		activationId = useActivationId();
		accountId = accountIdFromUser || undefined;
	}
	const entryPointParams = useMemo(
		() => ({ cloudId, accountId, activationId }),
		[cloudId, accountId, activationId],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncPlansSectionPlanListViewQuery,
		entryPointParams,
	);
	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.PLANS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();
	const menuId = MENU_ID_PLANS;

	// remove the entire useEffect when cleaning up nav4-show-plans-when-hidden-by-default
	// we should be using the route based entry point approach instead
	// https://hello.atlassian.net/wiki/spaces/UAF/pages/2754170095/Tutorial+EntryPoints+in+Routing
	// If starting life expanded, make sure to load.
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (isExpanded) {
			entryPointActions.load();
		}
	}, [isExpanded, entryPointActions]);

	const onSystemToggleCallBack = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
			}
		},
		[entryPointActions],
	);

	const onExpansionToggleCallBack = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.PLANS,
					sectionItem: SECTION_ITEM_PLANS,
				});
			}
		},
		[entryPointActions, sendSectionItemAnalyticsEvent],
	);

	const premiumLozenge = useMemoWithCondition(
		() => <PaidFeatureAwarenessPremiumLozenge />,
		[],
		() => fg('jfp-magma-4016-fix-side-nav-ssr-hydration'),
	);

	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			onSystemToggle={onSystemToggleCallBack}
			onExpansionToggle={onExpansionToggleCallBack}
			menuId={menuId}
		>
			<ExpandableMenuItemTrigger
				interactionName="nav4-sidebar-plans-default"
				actionsOnHover={actionsOnHover}
				aria-label={formattedMessage}
				elemBefore={icon}
				ref={triggerRef}
				testId={getTestId(menuId)}
			>
				<Flex gap="space.100" alignItems="center">
					{formattedMessage}
					<PaidFeatureAwarenessRenderer component={premiumLozenge} placement="navigation.plans" />
				</Flex>
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<JiraEntryPointContainer
					// Data stream, will subscribe to load updates
					entryPointReferenceSubject={entryPointReferenceSubject}
					// Required props for error JSErrorBoundary.
					id="nav4-plans-default"
					packageName={PACKAGE_NAME}
					// Optional props for error JSErrorBoundary.
					errorFallback="flag"
					teamName={TEAM_NAME}
					// Fallback for Suspense
					fallback={<SkeletonNavMenuItems />}
					// Props for the root component
					runtimeProps={entryPointParams}
				/>
				<Nav4PlansFooter />
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}
